import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer
} from 'recharts';
import AdminSidebar from '../components/AdminSidebar';
import api from '../api';

const FLAG = (code) => code
  ? String.fromCodePoint(...[...code.toUpperCase()].map(c => 0x1F1E0 - 65 + c.charCodeAt(0)))
  : '🌍';

const S = {
  page:      { fontFamily: 'var(--adm-font)' },
  topBar:    { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '.75rem' },
  pageTitle: { fontSize: '1.25rem', fontWeight: 700, color: 'var(--adm-text)' },
  pill:      { display: 'inline-flex', alignItems: 'center', gap: '.4rem', padding: '.38rem .85rem', borderRadius: 8, border: '1px solid var(--adm-border)', background: '#fff', fontSize: '.83rem', fontWeight: 500, cursor: 'pointer', color: 'var(--adm-text)' },
  livedot:   { width: 8, height: 8, borderRadius: '50%', background: '#28A745', flexShrink: 0 },

  statsGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 0, background: '#fff', border: '1px solid var(--adm-border)', borderRadius: 12, overflow: 'hidden', marginBottom: '1.25rem' },
  statCell:  { padding: '1.25rem 1.5rem', borderRight: '1px solid var(--adm-border)' },
  statLabel: { fontSize: '.8rem', color: 'var(--adm-text-sub)', fontWeight: 500, marginBottom: '.35rem' },
  statValue: { fontSize: '1.6rem', fontWeight: 700, color: 'var(--adm-text)', lineHeight: 1.1 },
  statSub:   { fontSize: '.78rem', color: 'var(--adm-text-sub)', marginTop: '.25rem' },

  chartCard: { background: '#fff', border: '1px solid var(--adm-border)', borderRadius: 12, padding: '1.25rem 1.5rem', marginBottom: '1.25rem' },
  chartTitle:{ fontSize: '.88rem', fontWeight: 600, color: 'var(--adm-text)', marginBottom: '1.25rem' },

  grid2:     { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.25rem', marginBottom: '1.25rem' },
  grid3:     { display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1.25rem', marginBottom: '1.25rem' },
  card:      { background: '#fff', border: '1px solid var(--adm-border)', borderRadius: 12, overflow: 'hidden' },
  cardHead:  { padding: '.9rem 1.25rem', borderBottom: '1px solid var(--adm-border)', fontSize: '.88rem', fontWeight: 600, color: 'var(--adm-text)' },
  cardBody:  { padding: '0 1.25rem' },
  row:       { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '.65rem 0', borderBottom: '1px solid var(--adm-border)', fontSize: '.85rem' },
  rowLabel:  { color: 'var(--adm-text-sub)' },
  rowValue:  { fontWeight: 600, color: 'var(--adm-text)' },
};

const buildChartData = (orders) => {
  const map = {};
  orders.forEach(o => {
    const d = new Date(o.created_at);
    const key = `${d.getMonth() + 1}/${d.getDate()}`;
    if (!map[key]) map[key] = { date: key, orders: 0, revenue: 0 };
    map[key].orders++;
    map[key].revenue += parseFloat(o.total_price || 0);
  });
  return Object.values(map).slice(-14);
};

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats]       = useState({ products: 0, orders: 0, revenue: 0, pending: 0, profit: 0 });
  const [visits, setVisits]     = useState({ total: 0, today: 0, week: 0, month: 0, byDevice: [], byPage: [], byCountry: [] });
  const [clicks, setClicks]     = useState({ total: 0, today: 0, byType: [], byProduct: [] });
  const [chartData, setChartData] = useState([]);
  const [recentOrders, setRecentOrders] = useState([]);
  const [visitPeriod, setVisitPeriod]   = useState('month');

  useEffect(() => {
    Promise.all([
      api.get('/api/admin/orders'),
      api.get('/api/products'),
      api.get('/api/visits/stats'),
      api.get('/api/clicks/stats'),
    ]).then(([ordersRes, productsRes, visitsRes, clicksRes]) => {
      const orders   = ordersRes.data.data || [];
      const products = productsRes.data.data || [];
      const active   = orders.filter(o => o.status !== 'cancelled');
      setStats({
        products: products.length,
        orders:   orders.length,
        revenue:  active.reduce((s, o) => s + parseFloat(o.total_price || 0), 0).toFixed(2),
        pending:  orders.filter(o => o.status === 'pending').length,
        profit:   active.reduce((s, o) => s + parseFloat(o.profit || 0), 0).toFixed(2),
      });
      setRecentOrders(orders.slice(0, 6));
      setChartData(buildChartData(orders));
      if (visitsRes.data.success) setVisits(visitsRes.data.data);
      if (clicksRes.data.success) setClicks(clicksRes.data.data);
    }).catch(console.error);
  }, []);

  const visitCount = { today: visits.today, week: visits.week, month: visits.month, total: visits.total }[visitPeriod] || 0;

  const paypalClicks = clicks.byType.find(t => t.button_type === 'paypal')?.count || 0;
  const cardClicks   = clicks.byType.find(t => t.button_type === 'card')?.count || 0;

  return (
    <div className="admin-layout">
      <AdminSidebar />
      <main className="admin-main" style={S.page}>

        {/* Top bar */}
        <div style={S.topBar}>
          <span style={S.pageTitle}>Overview</span>
          <div style={{ display: 'flex', gap: '.6rem', alignItems: 'center' }}>
            <span style={S.pill}>
              <span style={S.livedot}/>
              {visits.today} visitors today
            </span>
            <span style={S.pill}>
              🖱️ {clicks.today} clics aujourd'hui
            </span>
          </div>
        </div>

        {/* Stats row */}
        <div style={S.statsGrid}>
          {[
            { label: 'Sessions',    value: visitCount,             sub: 'store visits' },
            { label: 'Total sales', value: `${stats.revenue} MAD`, sub: 'active orders' },
            { label: 'Orders',      value: stats.orders,           sub: `${stats.pending} pending` },
            { label: 'Net profit',  value: `${stats.profit} MAD`,  sub: 'est. margin' },
            { label: 'Products',    value: stats.products,         sub: 'in catalogue' },
          ].map((s, i, arr) => (
            <div key={s.label} style={{ ...S.statCell, borderRight: i === arr.length - 1 ? 'none' : '1px solid var(--adm-border)' }}>
              <div style={S.statLabel}>{s.label}</div>
              <div style={S.statValue}>{s.value}</div>
              <div style={S.statSub}>{s.sub}</div>
            </div>
          ))}
        </div>

        {/* Clics boutons achat */}
        <div style={{ ...S.statsGrid, marginBottom: '1.25rem' }}>
          {[
            { label: ' Clics achat total', value: clicks.total,  sub: 'tous les boutons' },
            { label: ' Clics PayPal',       value: paypalClicks,  sub: 'bouton PayPal' },
            { label: ' Clics Carte bancaire',value: cardClicks,    sub: 'bouton Carte' },
          ].map((s, i, arr) => (
            <div key={s.label} style={{ ...S.statCell, borderRight: i === arr.length - 1 ? 'none' : '1px solid var(--adm-border)' }}>
              <div style={S.statLabel}>{s.label}</div>
              <div style={S.statValue}>{s.value}</div>
              <div style={S.statSub}>{s.sub}</div>
            </div>
          ))}
        </div>

        {/* Chart */}
        <div style={S.chartCard}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem' }}>
            <span style={S.chartTitle}>Orders over time</span>
            <div style={{ display: 'flex', gap: '.35rem' }}>
              {[['today','Today'],['week','7d'],['month','30d'],['total','All']].map(([k,l]) => (
                <button key={k} onClick={() => setVisitPeriod(k)} style={{
                  padding: '.28rem .7rem', borderRadius: 6, border: '1px solid var(--adm-border)',
                  background: visitPeriod === k ? '#1A1A1A' : '#fff',
                  color: visitPeriod === k ? '#fff' : 'var(--adm-text-sub)',
                  fontSize: '.78rem', fontWeight: 500, cursor: 'pointer', fontFamily: 'var(--adm-font)',
                }}>{l}</button>
              ))}
            </div>
          </div>
          {chartData.length > 0 ? (
            <ResponsiveContainer width="100%" height={200}>
              <LineChart data={chartData} margin={{ top: 4, right: 4, left: -20, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#F0F0F0" vertical={false} />
                <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#6D7175' }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: '#6D7175' }} axisLine={false} tickLine={false} allowDecimals={false} />
                <Tooltip
                  contentStyle={{ border: '1px solid #E3E3E3', borderRadius: 8, fontSize: 12, fontFamily: 'var(--adm-font)' }}
                  labelStyle={{ fontWeight: 600 }}
                />
                <Line type="monotone" dataKey="orders" stroke="#1A1A1A" strokeWidth={2} dot={false} activeDot={{ r: 4, fill: '#1A1A1A' }} />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div style={{ height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--adm-text-sub)', fontSize: '.875rem' }}>
              No order data yet
            </div>
          )}
        </div>

        {/* Pages + Devices + Countries */}
        <div style={S.grid3}>
          <div style={S.card}>
            <div style={S.cardHead}>Top pages</div>
            <div style={S.cardBody}>
              {visits.byPage.length === 0
                ? <div style={{ ...S.row, justifyContent: 'center', color: 'var(--adm-text-sub)' }}>No data</div>
                : visits.byPage.map(p => (
                  <div key={p.page} style={S.row}>
                    <span style={S.rowLabel}>{p.page}</span>
                    <span style={S.rowValue}>{p.count}</span>
                  </div>
                ))}
            </div>
          </div>

          <div style={S.card}>
            <div style={S.cardHead}>Devices</div>
            <div style={S.cardBody}>
              {visits.byDevice.length === 0
                ? <div style={{ ...S.row, justifyContent: 'center', color: 'var(--adm-text-sub)' }}>No data</div>
                : visits.byDevice.map(d => (
                  <div key={d.device} style={S.row}>
                    <span style={S.rowLabel}>{d.device === 'mobile' ? '📱 Mobile' : '🖥️ Desktop'}</span>
                    <span style={S.rowValue}>{d.count}</span>
                  </div>
                ))}
            </div>
          </div>

          <div style={S.card}>
            <div style={S.cardHead}>Countries</div>
            <div style={S.cardBody}>
              {visits.byCountry.length === 0
                ? <div style={{ ...S.row, justifyContent: 'center', color: 'var(--adm-text-sub)' }}>No data</div>
                : visits.byCountry.map(c => (
                  <div key={c.country} style={S.row}>
                    <span style={S.rowLabel}>{FLAG(c.country)} {c.country}</span>
                    <span style={S.rowValue}>{c.count}</span>
                  </div>
                ))}
            </div>
          </div>
        </div>

        {/* Clics par produit */}
        <div style={{ ...S.card, marginBottom: '1.25rem' }}>
          <div style={S.cardHead}>🖱️ Clics achat par produit</div>
          <div style={S.cardBody}>
            {clicks.byProduct.length === 0
              ? <div style={{ ...S.row, justifyContent: 'center', color: 'var(--adm-text-sub)' }}>Aucun clic enregistré</div>
              : clicks.byProduct.map(p => (
                <div key={p.product_id} style={S.row}>
                  <span style={S.rowLabel}>{p.name || `Produit #${p.product_id}`}</span>
                  <span style={S.rowValue}>{p.count} clics</span>
                </div>
              ))}
          </div>
        </div>

        {/* Recent orders */}
        <div style={S.card}>
          <div style={{ ...S.cardHead, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>Recent orders</span>
            <button onClick={() => navigate('/admin/orders')} style={{
              fontSize: '.8rem', fontWeight: 500, color: 'var(--adm-text-sub)',
              background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'var(--adm-font)',
            }}>View all</button>
          </div>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Order</th><th>Customer</th><th>Product</th><th>Total</th><th>Status</th><th>Date</th>
                </tr>
              </thead>
              <tbody>
                {recentOrders.length === 0 ? (
                  <tr><td colSpan={6} style={{ textAlign: 'center', color: 'var(--adm-text-sub)', padding: '2rem' }}>No orders yet</td></tr>
                ) : recentOrders.map(o => (
                  <tr key={o.id}>
                    <td style={{ fontWeight: 500 }}>#{o.id}</td>
                    <td>{o.customer_name}</td>
                    <td style={{ color: 'var(--adm-text-sub)' }}>{o.product_name}</td>
                    <td style={{ fontWeight: 500 }}>{parseFloat(o.total_price).toFixed(2)} MAD</td>
                    <td><span className={`badge badge-${o.status}`}>{o.status}</span></td>
                    <td style={{ color: 'var(--adm-text-sub)' }}>{new Date(o.created_at).toLocaleDateString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

      </main>
    </div>
  );
};

export default AdminDashboard;